import { computed } from 'vue';
import { queryAuthInfo, type AuthInfoResponse } from '@/api';
import { useUserStore } from '@/stores/user';
import { dynamicRouteState } from '@/router/routerState';

let authInitPromise: Promise<void> | null = null;
let lastInitToken: string | null = null;
let requestVersion = 0;

export const useAuth = () => {
  const userStore = useUserStore();

  const resetAuth = () => {
    requestVersion++;
    authInitPromise = null;
    lastInitToken = null;
    userStore.setAuthInfo();
    dynamicRouteState.removers
      .splice(0)
      .reverse()
      .forEach((remove) => remove());
    dynamicRouteState.added = false;
    dynamicRouteState.paths.length = 0;
  };

  const initAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      resetAuth();
      return Promise.resolve();
    }
    if (authInitPromise && lastInitToken === token) return authInitPromise;
    authInitPromise = null;
    lastInitToken = token;
    const currentVersion = ++requestVersion;
    authInitPromise = queryAuthInfo()
      .then((data: AuthInfoResponse) => {
        if (currentVersion !== requestVersion || lastInitToken !== token) return;
        userStore.setAuthInfo({
          agent_id: data.agent_id,
          app_id: data.app_id,
          app_code: data.app_code,
          username: data.username,
          nickname: data.nickname,
          role_name: data.role_name,
          role_code: data.role_code,
          is_online: data.is_online,
          public_msg_sound: data.public_msg_sound,
          personal_msg_sound: data.personal_msg_sound,
          notify_popup_seconds: data.notify_popup_seconds,
          vehicle_model_visible: data.vehicle_model_visible,
          can_view_plain_pii: data.can_view_plain_pii,
          visitor_inactive_hours: data.visitor_inactive_hours,
          permissions: data.permissions ?? [],
          menus: data.menus ?? [],
        });
      })
      .catch(() => {
        if (currentVersion !== requestVersion || lastInitToken !== token) return;
        // /auth/info 请求失败时仅清掉缓存允许下次重试，不清空已有的 menus/permissions
        // （401 等鉴权失败由响应拦截器统一跳转登录页）
        authInitPromise = null;
        lastInitToken = null;
      }) as Promise<void>;
    return authInitPromise;
  };

  /**
   * 强制重新拉取权限信息（绕过 initAuth 的同 token 缓存）。
   * 用于角色权限保存后即时同步——操作的可能正是自己所属角色，否则要手动刷新页面才生效。
   * 仅更新 permissions / menus，不重建动态路由：菜单增删仍需刷新页面。
   */
  const refreshAuth = () => {
    authInitPromise = null;
    lastInitToken = null;
    return initAuth();
  };

  return {
    currentUserRole: computed(() => userStore.roleKey),
    userPermissions: computed(() => userStore.permissions),
    hasPermission: userStore.hasPermission,
    initAuth,
    refreshAuth,
    resetAuth,
  };
};
